/** Минимальный markdown-рендер для help-страниц.
 *  Поддерживает: # h1, ## h2, ### h3, - списки, **bold**, _italic_,
 *  `code`, ```block```, ссылки [text](url), пустые строки = разделители.
 *  Не XSS-safe (мы рендерим только статичные .md из репо).
 */
import { Fragment, type ReactNode } from 'react';

function renderInline(line: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  while (i < line.length) {
    // ссылка [text](url)
    const lm = line.slice(i).match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (lm) {
      out.push(<a key={out.length} href={lm[2]} target="_blank" rel="noreferrer"
                  className="text-blue-400 hover:underline">{lm[1]}</a>);
      i += lm[0].length;
      continue;
    }
    // **bold**
    if (line.startsWith('**', i)) {
      const end = line.indexOf('**', i + 2);
      if (end > i) {
        out.push(<strong key={out.length}>{line.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // _italic_
    if (line[i] === '_') {
      const end = line.indexOf('_', i + 1);
      if (end > i) {
        out.push(<em key={out.length} className="text-slate-300">{line.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // `code`
    if (line[i] === '`') {
      const end = line.indexOf('`', i + 1);
      if (end > i) {
        out.push(<code key={out.length}
                       className="font-mono text-blue-300 bg-slate-950 px-1 rounded text-[0.9em]">
          {line.slice(i + 1, end)}
        </code>);
        i = end + 1;
        continue;
      }
    }
    // обычный символ
    const next = line.slice(i).search(/[*_`[]/);
    const stop = next === -1 ? line.length : i + next;
    if (stop > i) {
      out.push(<Fragment key={out.length}>{line.slice(i, stop)}</Fragment>);
      i = stop;
    } else {
      out.push(<Fragment key={out.length}>{line[i]}</Fragment>);
      i++;
    }
  }
  return out;
}

export function Markdown({ source }: { source: string }) {
  const lines = source.split('\n');
  const blocks: ReactNode[] = [];
  let listBuffer: string[] = [];
  let codeBlock: string[] | null = null;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    blocks.push(
      <ul key={blocks.length} className="list-disc pl-5 space-y-1 text-sm text-slate-200">
        {listBuffer.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
      </ul>
    );
    listBuffer = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (codeBlock !== null) {
      if (line.startsWith('```')) {
        blocks.push(
          <pre key={blocks.length}
               className="bg-slate-950 border border-slate-800 rounded p-3 text-xs font-mono text-slate-300 overflow-x-auto">
            {codeBlock.join('\n')}
          </pre>
        );
        codeBlock = null;
      } else {
        codeBlock.push(line);
      }
      continue;
    }
    if (line.startsWith('```')) { flushList(); codeBlock = []; continue; }
    if (line.startsWith('### ')) {
      flushList();
      blocks.push(<h3 key={blocks.length} className="text-base font-bold text-white mt-4 mb-1">{renderInline(line.slice(4))}</h3>);
      continue;
    }
    if (line.startsWith('## ')) {
      flushList();
      blocks.push(<h2 key={blocks.length} className="text-lg font-bold text-blue-300 mt-5 mb-2 border-b border-slate-800 pb-1">{renderInline(line.slice(3))}</h2>);
      continue;
    }
    if (line.startsWith('# ')) {
      flushList();
      blocks.push(<h1 key={blocks.length} className="text-2xl font-bold text-white mb-4">{renderInline(line.slice(2))}</h1>);
      continue;
    }
    if (line.match(/^[-*]\s+/)) {
      listBuffer.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }
    if (line.trim() === '') {
      flushList();
      continue;
    }
    flushList();
    blocks.push(<p key={blocks.length} className="text-sm text-slate-200 leading-relaxed break-words">{renderInline(line)}</p>);
  }
  flushList();
  // overflow-hidden + break-words на корне — чтобы длинные URL/пути/токены
  // не вылазили за пределы карточки.
  return (
    <div className="space-y-1 break-words"
         style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
      {blocks}
    </div>
  );
}
